import { findSelectedNode, trimScope, Scope } from '@bingads-webui-campaign-react/with-site-map';
import { getRouterDep } from '@bingads-webui-campaign-react/legacy-router-dep';
import { i18nMap, safeGetString } from '@bingads-webui-campaign-react/merchant-shared';

const storeNameBreadcrumbItem = (scope, storeName) => {
  const { storeId } = scope.values;
  return {
    key: storeName,
    text: storeName,
    onClick: () => {
      getRouterDep(scope).navigateToRoute('merchant_product_issues', { storeId });
    },
    'aria-label': storeName,
  };
};

const allStoresBreadcrumbItemWithoughtClick = (i18n) => {
  const allStoresText = i18n.getString(_TL_('Merchant Center'));

  return {
    key: 'stores',
    text: allStoresText,
    'aria-label': allStoresText,
  };
};

const allStoresBreadcrumbItem = (scope, i18n) => {
  const allStoresText = i18n.getString(_TL_('Merchant Center'));

  return {
    key: 'stores',
    text: allStoresText,
    onClick: () => {
      scope.remove('storeId');
      getRouterDep(scope).navigateToRoute('merchant_stores');
    },
    'aria-label': allStoresText,
  };
};

const summaryBreadcrumbItem = (scope, i18n) => {
  const { storeId } = scope.values;
  const summaryText = i18n.getString(_TL_('Summary'));

  return {
    key: 'summary',
    text: summaryText,
    onClick: () => {
      getRouterDep(scope).navigateToRoute('merchant_product_issues', { storeId });
    },
    'aria-label': summaryText,
  };
};

const feedDetailsItems = (props) => {
  const {
    i18n,
    scope,
    storeName,
    feedName,
  } = props;
  const { storeId } = scope.values;
  const feedsText = i18n.getString(_TL_('Feeds'));

  return [
    allStoresBreadcrumbItem(scope, i18n),
    storeNameBreadcrumbItem(scope, storeName),
    {
      key: 'feeds',
      text: feedsText,
      onClick: () => {
        getRouterDep(scope).navigateToRoute('merchant_feeds', { storeId });
      },
      'aria-label': feedsText,
    }, {
      key: 'feedName',
      text: feedName,
      'aria-label': feedName,
    }];
};

const createStoreItems = (props) => {
  const {
    i18n,
    scope,
  } = props;
  const merchantCenterText = i18n.getString(_TL_('Merchant Center'));
  const createStoreText = i18n.getString(_TL_('Create store'));

  return [{
    key: 'merchantcenter',
    text: merchantCenterText,
    onClick: () => {
      getRouterDep(scope).navigateToRoute('merchant_stores');
    },
    'aria-label': merchantCenterText,
  }, {
    key: 'mmc-create-store',
    text: createStoreText,
    'aria-label': createStoreText,
  }];
};

const importStepsItems = (props) => {
  const {
    i18n,
    scope: {
      values: {
        editMode,
      },
    },
  } = props;
  const importsText = i18n.getString(_TL_('Imports'));
  const createImportText = i18n.getString(_TL_('Create new import'));
  const updateImportText = i18n.getString(_TL_('Update import'));

  return editMode !== 'true' ? [{
    key: 'imports',
    text: importsText,
    'aria-label': importsText,
  }, {
    key: 'mmc-create-import',
    text: createImportText,
    isCurrentItem: true,
    'aria-label': createImportText,
  }] : [{
    key: 'imports',
    text: importsText,
    'aria-label': importsText,
  }, {
    key: 'mmc-update-import',
    text: updateImportText,
    isCurrentItem: true,
    'aria-label': updateImportText,
  }];
};

const placeholderStoresItems = (props) => {
  const {
    i18n,
    scope,
  } = props;
  const merchantCenterText = i18n.getString(_TL_('Merchant Center'));
  const placeholderStoresText = i18n.getString(_TL_('Draft stores'));

  return [{
    key: 'merchantcenter',
    text: merchantCenterText,
    onClick: () => {
      getRouterDep(scope).navigateToRoute('merchant_stores');
    },
    'aria-label': merchantCenterText,
  }, {
    key: 'mmc-placeholder-stores',
    text: placeholderStoresText,
    'aria-label': placeholderStoresText,
  }];
};

const editorialProductIssuesItems = (props) => {
  const {
    i18n,
    scope,
    storeName,
  } = props;
  const editorialProductIssuesText = i18n.getString(_TL_('Policy violation details'));

  return [
    allStoresBreadcrumbItem(scope, i18n),
    storeNameBreadcrumbItem(scope, storeName),
    summaryBreadcrumbItem(scope, i18n),
    {
      key: 'editorial-product-issues',
      text: editorialProductIssuesText,
      'aria-label': editorialProductIssuesText,
    },
  ];
};

const reviewRejectionsItems = (props) => {
  const {
    i18n,
    scope,
    storeName,
  } = props;
  const { storeId, flagAreaIdCode } = scope.values;
  const editorialProductIssuesText = i18n.getString(_TL_('Policy violation details'));
  const editorialProductIssueTitleText = safeGetString(i18n, i18nMap.IssueCodes, flagAreaIdCode);
  const params = { storeId };

  return [
    allStoresBreadcrumbItem(scope, i18n),
    storeNameBreadcrumbItem(scope, storeName),
    summaryBreadcrumbItem(scope, i18n),
    {
      key: 'editorial-product-issues',
      text: editorialProductIssuesText,
      onClick: () => {
        getRouterDep(scope).navigateToRoute('merchant_policy_violation_details', params);
      },
      'aria-label': editorialProductIssuesText,
    },
    {
      key: 'editorial-product-issue-title',
      text: editorialProductIssueTitleText,
      'aria-label': editorialProductIssueTitleText,
    },
  ];
};

export function getMerchantCenterConfig(props) {
  const {
    i18n,
    scope,
    storeName,
    siteMap,
  } = props;
  const { storeId } = scope.values;

  const maxDisplayedItems = 5;

  const currentPage = findSelectedNode(siteMap);
  const defaultBackButtonProps = {
    buttonText: i18n.getString(_TL_('Back')),
    onClick: (e) => {
      e.preventDefault();
      window.history.back();
    },
  };
  const defaultBreadcrumbsGeneralConfigs = {
    maxDisplayedItems,
    items: [
      allStoresBreadcrumbItem(scope, i18n),
      {
        key: storeName,
        text: storeName,
        onClick: () => {
          getRouterDep(scope).navigateToRoute('merchant_product_issues', { storeId });
        },
        'aria-label': storeName,
      }, {
        key: currentPage.id,
        text: i18n.getString(currentPage.name),
        'aria-label': i18n.getString(currentPage.name),
      }],
  };

  const defaultBreadcrumbsManageConfigs = {
    maxDisplayedItems,
    items: [
      allStoresBreadcrumbItem(scope, i18n)],
  };

  switch (currentPage.id) {
    case 'mmc-feed-details':
      return {
        backButtonProps: defaultBackButtonProps,
        breadcrumbsGeneralConfigs: {
          maxDisplayedItems,
          items: feedDetailsItems(props),
        },
      };
    case 'mmc-stores':
      return {
        backButtonProps: null,
        breadcrumbsGeneralConfigs: {
          maxDisplayedItems,
          items: [allStoresBreadcrumbItemWithoughtClick(i18n)],
        },
      };
    case 'mmc-create-store':
      return {
        backButtonProps: null,
        breadcrumbsGeneralConfigs: {
          maxDisplayedItems,
          items: createStoreItems(props),
        },
      };
    case 'mmc_placeholder_stores':
      return {
        backButtonProps: defaultBackButtonProps,
        breadcrumbsGeneralConfigs: {
          maxDisplayedItems,
          items: placeholderStoresItems(props),
        },
      };
    case 'mmc-import-steps':
      return {
        backButtonProps: {
          url: `/campaign/vnext/merchantcenter/import${trimScope([Scope.Store], scope)}`,
          buttonText: i18n.getString(_TL_('Back')),
        },
        breadcrumbsGeneralConfigs: {
          maxDisplayedItems,
          items: importStepsItems(props),
        },
      };
    case 'mmc-store-summary-productissuedetails':
      return {
        backButtonProps: defaultBackButtonProps,
        breadcrumbsGeneralConfigs: defaultBreadcrumbsGeneralConfigs,
      };
    case 'mmc-store-summary-policyviolationdetails':
      return {
        backButtonProps: defaultBackButtonProps,
        breadcrumbsGeneralConfigs: {
          maxDisplayedItems,
          items: editorialProductIssuesItems(props),
        },
      };
    case 'mmc-store-summary-reviewrejections':
      return {
        backButtonProps: defaultBackButtonProps,
        breadcrumbsGeneralConfigs: {
          maxDisplayedItems,
          items: reviewRejectionsItems(props),
        },
      };
    default:
      return {
        breadcrumbsGeneralConfigs: storeId ? defaultBreadcrumbsGeneralConfigs : defaultBreadcrumbsManageConfigs,
      };
  }
}
